import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { apiPath } from '../utils/appPaths';
import { PUBLIC_CONTENT_PAGE_MAX_WIDTH } from '../utils/publicPageLayout';

const WiringGuidesContainer = styled.div`
  width: min(${PUBLIC_CONTENT_PAGE_MAX_WIDTH}, calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  padding: clamp(24px, 4vw, 48px) 0 clamp(24px, 3vw, 36px);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-top: 30px;
`;

const WiringGuideCard = styled(Link)`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  text-decoration: none;
  color: inherit;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-color: #3b82f6;
  }
`;

const CardTitle = styled.h3`
  color: #1f2937;
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const CardSubtitle = styled.p`
  color: #6b7280;
  margin: 0 0 15px 0;
  font-size: 0.9rem;
`;

const CardDescription = styled.p`
  color: #374151;
  margin: 0 0 15px 0;
  line-height: 1.5;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #9ca3af;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.2rem;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 20px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  h3 {
    margin: 0 0 10px 0;
    font-size: 1.5rem;
  }
  
  p {
    margin: 0;
    font-size: 1rem;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const WiringGuides = () => {
  const [wiringGuides, setWiringGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWiringGuides();
  }, []);

  const fetchWiringGuides = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiPath('/wiring-guides'));
      
      if (response.ok) {
        const data = await response.json();
        setWiringGuides(data);
        setError(null);
      } else {
        setError('Failed to load wiring');
      }
    } catch (err) {
      console.error('Error fetching wiring:', err);
      setError('Failed to load wiring');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <WiringGuidesContainer>
        <LoadingContainer>Loading wiring...</LoadingContainer>
      </WiringGuidesContainer>
    );
  }

  if (error) {
    return (
      <WiringGuidesContainer>
        <ErrorContainer>{error}</ErrorContainer>
      </WiringGuidesContainer>
    );
  }

  return (
    <WiringGuidesContainer>
      <Header>
        <Title>Wiring</Title>
        <Subtitle>Step-by-step wiring instructions for connecting sensors to microcontrollers</Subtitle>
      </Header>

      {wiringGuides.length === 0 ? (
        <EmptyState>
          <h3>No wiring available</h3>
          <p>Check back later for new wiring.</p>
        </EmptyState>
      ) : (
        <Grid>
          {wiringGuides.map((guide) => (
            <WiringGuideCard key={guide.id} to={`/wiring-guide/${guide.slug}`}>
              <CardTitle>{guide.sensor_name} to {guide.microcontroller_name}</CardTitle>
              <CardSubtitle>Wiring</CardSubtitle>
              {guide.description && (
                <CardDescription>{guide.description}</CardDescription>
              )}
              <CardMeta>
                <span>Created {formatDate(guide.created_at)}</span>
                <span>View Wiring -></span>
              </CardMeta>
            </WiringGuideCard>
          ))}
        </Grid>
      )}
      <Copyright>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Copyright>
    </WiringGuidesContainer>
  );
};

export default WiringGuides;
